import { useEffect, useState } from "react";
import type { BrandingTemplate } from "@vms/types";

/**
 * Token-gated fetch of a leaderboard's branding template. Used by the
 * public embed page — anonymous (no JWT), so it can't hit the auth'd
 * `/api/branding-templates/:id` endpoint.
 *
 * Talks to `/api/leaderboard/saved/:id/branding` instead, which validates
 * the same display-access token used by `/saved/:id/config` and
 * `/saved/:id/entries`. The endpoint returns `null` for leaderboards with
 * no branding template assigned (renderer falls through to the default).
 */
export function useBrandingTemplatePublic(
  leaderboardId: string | null,
  token: string | null,
): { data: BrandingTemplate | null } {
  const [data, setData] = useState<BrandingTemplate | null>(null);
  useEffect(() => {
    if (!leaderboardId || !token) {
      setData(null);
      return;
    }
    let cancelled = false;
    void fetch(`/api/leaderboard/saved/${leaderboardId}/branding`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j?.success) {
          setData((j.data as BrandingTemplate | null) ?? null);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [leaderboardId, token]);
  return { data };
}
